/**
 * "Free up counter time" — pick existing counter assignments to clear.
 *
 * Grouped the way the officer-graph filter groups its rows, because a night roster
 * can run to forty-odd officers and a flat list of them is unusable: officer type
 * first, then a row per block of ten, then the officers themselves and their actual
 * assignment windows. Every level is a checkbox that ticks everything beneath it and
 * shows indeterminate when only part of it is on, so "all the OT officers" or
 * "S11 to S20" is one click rather than twenty.
 *
 * It only picks. What happens to the picked windows is the caller's business — in the
 * auto-assign modal they are cleared and re-planned in the same batch, which is why
 * the selection is a controlled prop rather than state of its own.
 */

import { useMemo, useState } from 'react';
import { formatCounterLabel, naturalSortOfficers } from '@/config/modeConfig';
import { useOfficerOrder } from '@/utils/officerOrder';
import { groupOfficersByType, officerTypeOf, OFFICER_TYPE_PILL } from '@/utils/officerGrouping';
import type { OfficerMetadata } from '@/types/api';

/** Officers per sub-group. Matches the officer-graph filter, so the two read alike. */
const GROUP_SIZE = 10;

export interface AssignmentWindow {
  startSlot: number;
  endSlot: number;
  counter: number;
  start: string;
  end: string;
}

interface Props {
  /** Every officer's current per-slot counter assignments. */
  officerSchedules: Record<string, number[]>;
  officerMetadata: Record<string, OfficerMetadata>;
  /** Slot → "HHMM", so a window can name its own times. */
  timeLabels: string[];
  operationMode: string | undefined;
  /** Selected window keys, "officerKey#windowIndex". Controlled by the caller. */
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Collapses an officer's schedule into contiguous same-counter windows. */
  deriveWindows: (schedule: number[], timeLabels: string[]) => AssignmentWindow[];
}

// Not exported: nothing outside this file needs a window's key, and exporting a plain
// function alongside the component would stop Fast Refresh treating this as a
// component-only module.
const windowKey = (officerId: string, index: number) => `${officerId}#${index}`;

function fmtHHMM(hhmm: string): string {
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
}

export function ClearAssignmentsPicker({
  officerSchedules, officerMetadata, timeLabels, operationMode,
  selected, onChange, deriveWindows,
}: Props) {
  const [search, setSearch] = useState('');
  const officerOrder = useOfficerOrder();   // re-sort when the user drags rows

  // Officers that have something to clear, in officer-type groups of ten — the
  // groups themselves in the SAME order the officer graph lists officers in
  // (groupOfficersByType), not a second, independently-chosen ordering.
  const groups = useMemo(() => {
    const withWindows = naturalSortOfficers(Object.keys(officerSchedules))
      .map(id => ({ id, windows: deriveWindows(officerSchedules[id] ?? [], timeLabels) }))
      .filter(o => o.windows.length > 0);

    return groupOfficersByType(
      withWindows, o => o.id, o => officerTypeOf(o.id, officerMetadata[o.id]?.officer_type),
    ).map(({ type, items: officers }) => {
      const blocks = [];
      for (let i = 0; i < officers.length; i += GROUP_SIZE) {
        const slice = officers.slice(i, i + GROUP_SIZE);
        blocks.push({ label: `${slice[0].id}–${slice[slice.length - 1].id}`, officers: slice });
      }
      return { type, label: type === 'MAIN' ? 'Main' : type, officers, blocks };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officerSchedules, officerMetadata, timeLabels, deriveWindows, officerOrder]);

  const q = search.trim().toLowerCase();
  const matches = (id: string): boolean => {
    if (!q) return true;
    const m = officerMetadata[id];
    const nameLabel = [m?.deployment, m?.name].filter(Boolean).join(' - ').toLowerCase();
    return id.toLowerCase().includes(q) || nameLabel.includes(q);
  };

  /** Every window key under a set of officers — what a group-level checkbox drives. */
  const keysOf = (officers: Array<{ id: string; windows: AssignmentWindow[] }>): string[] =>
    officers.flatMap(o => o.windows.map((_, i) => windowKey(o.id, i)));

  const stateOf = (keys: string[]): 'all' | 'some' | 'none' => {
    const n = keys.filter(k => selected.has(k)).length;
    return n === 0 ? 'none' : n === keys.length ? 'all' : 'some';
  };

  const toggleKeys = (keys: string[]) => {
    const next = new Set(selected);
    if (keys.every(k => next.has(k))) keys.forEach(k => next.delete(k));
    else keys.forEach(k => next.add(k));
    onChange(next);
  };

  const checkbox = (keys: string[], accent = '#dc2626') => {
    const st = stateOf(keys);
    return (
      <input
        type="checkbox"
        checked={st === 'all'}
        ref={el => { if (el) el.indeterminate = st === 'some'; }}
        onChange={() => toggleKeys(keys)}
        style={{ accentColor: accent, cursor: 'pointer', flexShrink: 0, margin: 0 }}
      />
    );
  };

  if (groups.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#64748b', padding: '4px 6px' }}>
        No officer has any counter assignment to clear.
      </div>
    );
  }

  const allKeys = groups.flatMap(g => keysOf(g.officers));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search officer…"
          style={{
            flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '4px 8px', fontSize: 12,
            border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', color: '#0f172a',
          }}
        />
        <button
          onClick={() => onChange(stateOf(allKeys) === 'all' ? new Set() : new Set(allKeys))}
          style={{
            background: 'none', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', padding: '2px 4px', flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          {stateOf(allKeys) === 'all' ? 'Clear none' : 'Select all'}
        </button>
      </div>

      {groups.map(group => {
        const visibleBlocks = group.blocks
          .map(b => ({ ...b, officers: b.officers.filter(o => matches(o.id)) }))
          .filter(b => b.officers.length > 0);
        if (q && visibleBlocks.length === 0) return null;
        const groupKeys = keysOf(group.officers);
        const pill = OFFICER_TYPE_PILL[group.type];
        return (
          <div key={group.type} style={{ marginBottom: 8 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0',
              cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#334155',
            }}>
              {checkbox(groupKeys)}
              <span style={{
                fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                background: pill.bg, color: pill.fg,
              }}>{group.label}</span>
              <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>
                {groupKeys.filter(k => selected.has(k)).length}/{groupKeys.length} selected
              </span>
            </label>

            {visibleBlocks.map(block => {
              const blockKeys = keysOf(block.officers);
              return (
                <div key={block.label} style={{ marginLeft: 16 }}>
                  {group.blocks.length > 1 && (
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '2px 0',
                      cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#64748b',
                    }}>
                      {checkbox(blockKeys)}
                      {block.label}
                    </label>
                  )}
                  {block.officers.map(({ id, windows }) => {
                    const m = officerMetadata[id];
                    const nameLabel = [m?.deployment, m?.name].filter(Boolean).join(' - ');
                    const officerKeys = windows.map((_, i) => windowKey(id, i));
                    return (
                      <div key={id} style={{ marginLeft: group.blocks.length > 1 ? 14 : 0 }}>
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0',
                          cursor: 'pointer', fontSize: 11, color: '#0f172a',
                        }}>
                          {checkbox(officerKeys)}
                          <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{id}</span>
                          {nameLabel && (
                            <span style={{
                              color: '#64748b', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{nameLabel}</span>
                          )}
                        </label>
                        {windows.map((w, idx) => {
                          const key = windowKey(id, idx);
                          return (
                            <label key={key} style={{
                              display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                              fontSize: 11, color: '#334155', padding: '2px 0 2px 20px',
                            }}>
                              <input
                                type="checkbox"
                                checked={selected.has(key)}
                                onChange={() => toggleKeys([key])}
                                style={{ accentColor: '#dc2626', cursor: 'pointer', flexShrink: 0, margin: 0 }}
                              />
                              <span style={{ fontFamily: 'monospace' }}>
                                {fmtHHMM(w.start)}–{fmtHHMM(w.end)}
                              </span>
                              <span style={{ fontWeight: 700 }}>
                                {formatCounterLabel(w.counter, operationMode)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
      {q && groups.every(g => g.officers.every(o => !matches(o.id))) && (
        <div style={{ fontSize: 11, color: '#64748b', padding: '6px 6px' }}>
          No officers match “{search.trim()}”.
        </div>
      )}
    </div>
  );
}
